import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';

import api from '@/services/api';

import Pagination from '@/utils/pagination/Pagination';

import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';
import DepartmentsSearchFilters from '@/views/pages/Departments/elements/DepartmentsSearchFilters';
import DepartmentsContainer from '@/views/pages/Departments/elements/DepartmentsContainer';

import ModalDeleteItem from '@/views/pages/Departments/modals/ModalDeleteItem';
import ModalUploadDepartments from '@/views/pages/Departments/modals/ModalUploadDepartments';
import ModalAddDepartment from '@/views/pages/Departments/modals/ModalAddDepartment';
import ModalUploadHeader from '@/views/pages/Departments/modals/ModalUploadHeader';
import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import { CheckPermission, CanHandleDepartments } from '@/utils/permissions/permissions';

import styles from '@/views/pages/Departments/Departments.module.scss';

const Departments = () => {
	const { dict, mode } = useSelector((state) => state.app);
	const { user } = useSelector((state) => state.auth);
	const location = useLocation();
	const navigate = useNavigate();

	const sortMethods = [
		{ value: 'name', label: dict.sortOptions.departmentName },
		{ value: '-name', label: dict.sortOptions.departmentNameReverse },
	];

	const [ depData, setDepData ] = useState(null);
	const [ pagesNum, setPagesNum ] = useState(null);
	const [ lettersNum, setLettersNum ] = useState(0);

	const [ sortOn, setSortOn ] = useState(sortMethods[0]);
	const [ isLoading, setIsLoading ] = useState(true);

	const [ itemToDelete, setItemToDelete ] = useState(null);
	const [ itemToEdit, setItemToEdit ] = useState(null);
	const [ showModalDeleteItem, setShowModalDeleteItem ] = useState(false);
	const [ showModalUpload, setShowModalUpload ] = useState(false);
	const [ showModalAddDepartment, setShowModalAddDepartment ] = useState(false);
	const [ showModalHeaderFooter, setShowModalHeaderFooter ] = useState({show: false, header: true});

	const { search: locationSearch, pathname: locationPathName } = location;
	
	const changeSort = useCallback((element) => {
		const searchParams = queryString.parse(locationSearch);
		const copiedSearchParams = Object.assign({}, searchParams);
	
		setSortOn(element);
		navigate(`${locationPathName}?${parseObjectToQuery({ ...copiedSearchParams, ord: element.value })}`);
	}, [ setSortOn ]);

	const resetSort = useCallback(() => {
		setSortOn(sortMethods[0]);
	}, [ setSortOn ]);

	useEffect(() => {
		const searchParams = queryString.parse(locationSearch);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (copiedSearchParams.ord) {
			setSortOn(sortMethods.find((el) => { return el.value === copiedSearchParams.ord; }));
		} else {
			setSortOn(sortMethods[0]);
		}
	}, [ location ]);

	const fetchData = ({ data }) => {
		if (data) {
			setDepData(data.departments);
			setPagesNum(parseInt(data.pages));
			setLettersNum(data.hits);
		}
	};

	const reloadDepartments = useCallback(() => {
		// if there is no page in location don't fetch data
		const qu = Object.fromEntries(new URLSearchParams(locationSearch));

		setIsLoading(true);
		api.get(`/departments?${parseObjectToQuery(qu)}`).then(res => {
			fetchData(res);
			setIsLoading(false);
		});
	}, [ location ]);

	useEffect(() => {
		reloadDepartments();
		const qu = Object.fromEntries(new URLSearchParams(locationSearch));
		if (Object.hasOwn(qu, 'ord')) {
			const foundElement = sortMethods.find(item => item.value === qu.ord);
			if (foundElement) return setSortOn(foundElement);
			setSortOn(sortMethods[0]);
		} else {
			setSortOn(sortMethods[0]);
		}
		// eslint-disable-next-line
    }, [location, dict]);

	const onDeleteItem = (data) => {
		setItemToDelete(data);
	};

	const onEditItem = (data) => {
		setItemToEdit(data);
	};

	return (
		<div className={ styles['departments__wrapper'] }>

			<Header narrow={ false } />

			<ModalDeleteItem
				showModalDeleteItem={ showModalDeleteItem }
				setShowModalDeleteItem={ setShowModalDeleteItem }
				itemToDelete={ itemToDelete }
				setItemToDelete={ setItemToDelete }
				reloadDepartments={ reloadDepartments }
			/>
			<ModalUploadDepartments 
				reloadDepartments={ reloadDepartments }
				showModalUpload={ showModalUpload }
				setShowModalUpload={ setShowModalUpload }
				setItemToEdit={ setItemToEdit }
			/>
			{showModalAddDepartment && <ModalAddDepartment 
				itemToEdit={ itemToEdit }
				setItemToEdit={ setItemToEdit }
				reloadDepartments={ reloadDepartments }
				showModalAddDepartment={ showModalAddDepartment }
				setShowModalAddDepartment={ setShowModalAddDepartment }
			/>}
			{showModalHeaderFooter.show && <ModalUploadHeader 
				itemToEdit={ itemToEdit }
				setItemToEdit={ setItemToEdit }
				reloadDepartments={ reloadDepartments }
				showModalHeaderFooter={ showModalHeaderFooter }
				setShowModalHeaderFooter={ setShowModalHeaderFooter }
			/>}
			
			<main className={ styles['departments__main'] }>
				<h1 className={ styles['departments__header'] }>{dict.departments.departments}
					{lettersNum > 0 && (
						<span className={ styles['departments__header--number'] }>&nbsp;({lettersNum})</span>)}
				</h1>
				<div className={ styles['departments__description'] }>
					<div className={ styles['departments__description--text'] }>
						{user.role === 'admin' && dict.departments.description1}
						{user.role !== 'admin' && dict.departments.description2}
					</div>
					{CheckPermission("canAddDepartments", user.role) && <div className={ styles['departments__description--buttons'] }>
						<button className='button__blue'
							onClick={ () => setShowModalUpload(true) }
						>
							<i className='icon-upload' />
							{dict.departments.buttons.loadFile}
						</button>
						<button
							className='button__blue'
							onClick={ () => setShowModalAddDepartment(true) }
						>+ {dict.departments.buttons.addDep}
						</button>
					</div>}
				</div>
				{CanHandleDepartments(user.role, mode) &&
					<div className={ styles['departments__filters--wrapper'] }>
						<DepartmentsSearchFilters resetSort={ resetSort } />
					</div>
				}
				<Pagination
					pagesNum={ pagesNum }
					sortingOptions={ CanHandleDepartments(user.role, mode) ? sortMethods : undefined }
					sortingValue={ sortOn }
					sortingOnChange={ changeSort }
				>
					<DepartmentsContainer
						depData={ depData }
						isLoading={ isLoading }
						onEditItem={ onEditItem }
						setShowModalAddDepartment= { setShowModalAddDepartment }
						onDeleteItem={ onDeleteItem }
						setShowModalDeleteItem={ setShowModalDeleteItem }
						setShowModalHeaderFooter={ setShowModalHeaderFooter }
					/>
				</Pagination>
			</main>

			<Footer narrow={ false } />
		</div>
	);
};
export default React.memo(Departments);